import { LitElement, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';

import { emit } from '@amityeko/pbc-core/dist/utilities/event';
import { getFormattedTime } from '@amityeko/pbc-core/dist/utilities/datetime';

import closeIcon from '@amityeko/design-system/dist/assets/icons/close';

import '@amityeko/design-system/dist/elements/button';
import '@amityeko/design-system/dist/elements/icon';
import '@amityeko/design-system/dist/elements/typography/tg-caption';

import styles from './styles';

/**
 * @dependency amityeko-button
 * @dependency amityeko-icon
 * @dependency amityeko-tg-caption
 *
 * @slot default - Element that triggers the dialog with Empty View
 */
@customElement('rnr-audio')
export default class RnRAudio extends LitElement {
  static styles = styles;

  @query('#audio') audio!: HTMLAudioElement;
  @query('#inner') inner!: HTMLDivElement;

  @property()
  audioURL?: string = 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3';

  @property({ type: Boolean })
  shouldShowRemainingTime: boolean = false;

  @property({ type: Boolean })
  shouldShowCancelBtn: boolean = false;

  @property()
  theme: 'light' | 'dark' = 'dark';

  @state()
  remainingTime?: string;

  @state()
  isPlaying: boolean = false;

  private _play() {
    if (this.audio.paused) {
      this.audio.play();
      this.isPlaying = true;
    } else {
      this.audio.pause();
      this.isPlaying = false;
    }
  }

  private _onTimeUpdate() {
    if (this.audio.ended) {
      this.isPlaying = false;
    }

    const remainingTime = this.audio.duration - this.audio.currentTime;
    this.remainingTime = getFormattedTime(remainingTime);
    let width = (this.audio.currentTime / this.audio.duration) * 100;
    this.inner.style.width = `${width}%`;
  }

  private _onCancelAudio() {
    emit(this, 'rnr-cancel-audio');
  }

  protected render() {
    if (!this.audioURL) return;

    return html`<div class="container">
      <audio
        class="audio"
        id="audio"
        @timeupdate=${this._onTimeUpdate}
        controls
      >
        <source
          src=${this.audioURL}
          type="audio/mp3"
        />
        Your browser does not support the audio tag.
      </audio>
      <div class="audio__wrapper" 
                style=${styleMap({
                  '--private-audio-bg-color':
                    this.theme === 'dark' ? '#ffffff' : 'rgba(255, 255, 255, 0.3)',
                })}>
        <div class="play" @click=${this._play}>
            <div
                class=${classMap({
                  button: true,
                  'button--pause': this.isPlaying,
                })}
                style=${styleMap({
                  '--private-play-btn-color': this.theme === 'dark' ? '#000000' : '#ffffff',
                })}
                > 
            </div>
        </div>
        <div class="timeline__wrapper">
            <div class="timeline"  style=${styleMap({
              '--private-timeline-color': this.theme === 'dark' ? '#e5e5e5' : '#ffffff',
            })}>
            <div class="bar">
                <div id="inner" class="inner"></div>
                <div class="progress__circle"></div>
            </div>
            </div>
        </div>  
        ${
          this.shouldShowRemainingTime && this.remainingTime
            ? html`<amityeko-tg-caption style=${styleMap({
                '--private-remaining-time-color': this.theme === 'dark' ? '#e5e5e5' : '#ffffff',
              })}>${this.remainingTime}</amityeko-tg-caption></amityeko-icon>`
            : null
        }
        ${
          this.shouldShowCancelBtn
            ? html`
                <amityeko-button @click=${this._onCancelAudio} variant="ghost" ?circle=${true}>
                  <amityeko-icon .iconSize=${'12px'}>${closeIcon}</amityeko-icon>
                </amityeko-button>
              `
            : null
        }
      </div>
      </div>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rnr-audio': RnRAudio;
  }
}
